import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger({ module: 'usePolling' });

/** Number of consecutive poll failures before surfacing the error to the
 *  caller. A single transient 500 or network hiccup should not kill an
 *  in-progress chat session — only sustained failures indicate a real
 *  problem. At the default 1 s interval this means ~3 s of tolerance. */
const MAX_CONSECUTIVE_ERRORS = 3;

/** Hard wall-clock ceiling for a single task's polling. An unrecognized task_id
 *  makes the progress endpoint return HTTP 200 PENDING indefinitely, which no
 *  termination path catches; without a ceiling the chat spins forever. 20 min sits
 *  above the interactive reasoning chain hard time_limit (780s,
 *  apps/reasoning/tasks.py:1505) so a legitimately long turn is never falsely
 *  failed, and below the stuck-chain reaper's queued cutoff (3600s,
 *  apps/reasoning/tasks.py:2473) so the client gives up well before any
 *  server-side resolution could matter. */
export const MAX_POLL_DURATION_MS = 20 * 60 * 1000;

interface UsePollingOptions {
  enabled: boolean;
  interval?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Polling data structure varies by task type
  onUpdate?: (data: any) => void;
  onError?: (error: string) => void;
}

export function usePolling(
  taskId: string | null,
  conversationId: string | null,
  orgId: string | undefined,
  options: UsePollingOptions
) {
  const { enabled, interval = 1000, onUpdate, onError } = options;
  const [isPolling, setIsPolling] = useState(false);

  // Ref-capture callbacks so pollProgress stays identity-stable and the
  // interval effect doesn't restart on parent re-renders. Same pattern as
  // useChatProgress's ref-captured WS callbacks.
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  });
  useEffect(() => {
    onErrorRef.current = onError;
  });

  // Track consecutive poll failures so transient errors (a single 500,
  // a brief network blip) don't immediately surface to the caller.
  const consecutiveErrorsRef = useRef(0);

  // Set when the wall-clock cap fires. Once capped, drop any late in-flight poll
  // response (a 200 must not forward an update; a rejection must not touch the
  // error budget or fire a second onError) — same drop-stale-response intent as
  // activeTaskIdRef, but for the timed-out case where taskId is unchanged.
  const pollingCappedRef = useRef(false);

  // Always holds the currently-active taskId. A request dispatched for an
  // earlier task can resolve AFTER taskId changes (the interval is cleared on
  // cleanup, but an in-flight fetch is not), and it would otherwise fire the
  // ref-captured latest onUpdate/onError for a task that is no longer active —
  // e.g. a superseded discussion turn's completion tick stopping the new turn's
  // polling and applying the wrong state. Comparing the request's own taskId to
  // this ref lets us drop such stale responses.
  const activeTaskIdRef = useRef<string | null>(taskId);
  useEffect(() => {
    activeTaskIdRef.current = taskId;
  });

  // Identifies the current polling session (task + conversation). The
  // wall-clock deadline is anchored to when this session began, so a
  // fallback-interval change (e.g. a WebSocket reconnect flipping the poll
  // cadence) re-runs the effect WITHOUT resetting the ceiling — otherwise a
  // flapping socket could defer the deadline forever.
  const sessionKeyRef = useRef<string | null>(null);
  const sessionStartRef = useRef(0);

  const pollProgress = useCallback(async () => {
    if (!taskId || !conversationId || !orgId || !enabled) {
      return;
    }

    try {
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/progress/${taskId}/`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Drop the response if this request's task is no longer the active one —
      // a stale in-flight poll must not fire callbacks for a superseded task.
      if (taskId !== activeTaskIdRef.current) {
        return;
      }

      if (pollingCappedRef.current) {
        return;
      }

      if (response.ok) {
        consecutiveErrorsRef.current = 0;
        const data = await response.json();
        // Re-check after the parse await: the watchdog may have fired (capping
        // polling) or the task may have been superseded while response.json()
        // was settling. A late success must not update the chat post-timeout.
        if (pollingCappedRef.current || taskId !== activeTaskIdRef.current) {
          return;
        }
        onUpdateRef.current?.(data);
      } else if (response.status === 404) {
        consecutiveErrorsRef.current = 0;
        // Task not found - might be completed or expired
        onUpdateRef.current?.({
          status: 'completed',
          message: 'Task completed or not found',
        });
      } else {
        consecutiveErrorsRef.current += 1;
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
          onErrorRef.current?.("We couldn't fetch progress. Try again.");
        } else {
          logger.warn(
            `Poll returned ${response.status}, retrying (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS})`
          );
        }
      }
    } catch (err) {
      // A stale request that failed must not touch the active task's error
      // budget or surface an error for a task that is no longer active.
      if (taskId !== activeTaskIdRef.current) {
        return;
      }
      if (pollingCappedRef.current) {
        return;
      }
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        logger.error('Network error while polling', err as Error);
        onErrorRef.current?.('Network error while polling');
      } else {
        logger.warn(
          `Network error while polling, retrying (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS})`
        );
      }
    }
  }, [taskId, conversationId, orgId, enabled]);

  useEffect(() => {
    if (!enabled || !taskId || !conversationId) {
      setIsPolling(false);
      consecutiveErrorsRef.current = 0;
      pollingCappedRef.current = false;
      sessionKeyRef.current = null;
      return;
    }

    // A new polling session resets the per-session budget and anchors the
    // wall-clock ceiling. An interval-only change keeps the same session, so
    // the anchor (and the cap) survive re-runs.
    const sessionKey = `${taskId}::${conversationId}`;
    if (sessionKeyRef.current !== sessionKey) {
      sessionKeyRef.current = sessionKey;
      sessionStartRef.current = Date.now();
      consecutiveErrorsRef.current = 0;
      pollingCappedRef.current = false;
    }

    // If the ceiling for this session already elapsed (e.g. the effect re-ran
    // after a fallback-interval change past the deadline), stay capped: do not
    // resume polling or re-arm the watchdog (which would re-fire onError).
    if (pollingCappedRef.current) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    // Start polling immediately
    pollProgress();

    const intervalId = setInterval(pollProgress, interval);

    // Independent wall-clock ceiling, anchored to the session start rather than
    // this effect run: a hung fetch must not defer the deadline, and neither
    // must a fallback-interval change. Mirrors approvalExecutionTracker's
    // MAX_WALL_MS (frontend/src/lib/proposals/realtime/approvalExecutionTracker.ts:20-24,111-114).
    const remaining = Math.max(
      0,
      MAX_POLL_DURATION_MS - (Date.now() - sessionStartRef.current)
    );
    const deadlineId = setTimeout(() => {
      pollingCappedRef.current = true;
      clearInterval(intervalId);
      setIsPolling(false);
      onErrorRef.current?.('The assistant timed out. Please try again.');
    }, remaining);

    return () => {
      clearInterval(intervalId);
      clearTimeout(deadlineId);
      setIsPolling(false);
    };
  }, [enabled, taskId, conversationId, interval, pollProgress]);

  return { isPolling };
}
